<template>
  <div class="w-full h-auto bg-white rounded-lg shadow dark:bg-gray-800 p-4 md:p-6">
    <div class="self-stretch flex justify-between items-center">
      <div class="grow shrink basis-0 h-5 flex items-center">
        <div
          class="grow shrink basis-0 h-5 text-neutral-900 text-lg font-semibold font-['Public Sans'] leading-7"
        >
          Next Tasks Due Date
        </div>
      </div>
      <div
        class="pl-3.5 pr-2.5 py-1.5 rounded-lg border border-slate-200 flex justify-center items-center gap-2"
      >
        <div
          class="text-slate-600 text-sm font-normal font-['Public Sans'] leading-snug"
        >
          Next 7 days
        </div>
      </div>
    </div>
    <div>
      <apexchart
        type="area"
        height="350"
        :options="chartOptions"
        :series="series"
      ></apexchart>
    </div>
  </div>
</template>

<script>
import { ref, computed, onMounted } from "vue";
import { useStore } from "vuex";
import ApexCharts from "vue3-apexcharts";

const MAX_TASKS_IN_TOOLTIP = 10;

const escapeHtml = (value) =>
  String(value ?? "")
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#39;");

export default {
  components: {
    apexchart: ApexCharts,
  },
  setup() {
    const store = useStore();
    const days = ref([]);

    const series = computed(() => [
      {
        name: "Tasks Due",
        data: days.value.map((d) => d.count),
      },
    ]);

    // Tooltip: tareas del día agrupadas por proyecto
    const renderTooltip = ({ dataPointIndex }) => {
      const day = days.value[dataPointIndex];
      if (!day) return "";

      const header = `<div style="padding:6px 10px;background:#f1f5f9;border-bottom:1px solid #e2e8f0;font-weight:600;">
          ${escapeHtml(day.label)} · ${day.count} task${day.count === 1 ? "" : "s"} due
        </div>`;

      if (!day.tasks.length) {
        return `<div style="min-width:180px;font-size:12px;">${header}
          <div style="padding:6px 10px;color:#64748b;">No tasks due</div></div>`;
      }

      const shown = day.tasks.slice(0, MAX_TASKS_IN_TOOLTIP);
      const byProject = shown.reduce((acc, t) => {
        const key = t.project || "No project";
        (acc[key] = acc[key] || []).push(t.name);
        return acc;
      }, {});

      const body = Object.entries(byProject)
        .map(
          ([project, names]) => `<div style="margin-bottom:6px;">
            <div style="font-weight:600;color:#1d4ed8;">${escapeHtml(project)}</div>
            ${names
              .map((n) => `<div style="padding-left:8px;color:#334155;">• ${escapeHtml(n)}</div>`)
              .join("")}
          </div>`
        )
        .join("");

      const more =
        day.tasks.length > shown.length
          ? `<div style="color:#64748b;">+${day.tasks.length - shown.length} more</div>`
          : "";

      return `<div style="min-width:200px;max-width:320px;font-size:12px;text-align:left;white-space:normal;">
          ${header}<div style="padding:6px 10px;">${body}${more}</div>
        </div>`;
    };

    const chartOptions = ref({
      chart: {
        type: "area",
        height: 350,
        zoom: { enabled: false },
      },
      dataLabels: { enabled: false },
      stroke: { curve: "smooth" },
      colors: ["#f59e0b"],
      labels: [],
      xaxis: { type: "category" },
      yaxis: {
        opposite: false,
        min: 0,
        forceNiceScale: true,
        labels: { formatter: (v) => Math.round(v) },
      },
      tooltip: {
        custom: renderTooltip,
      },
    });

    onMounted(async () => {
      const userTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
      const response = await store.dispatch("fetchTasksByDueDate", {
        timezone: userTimezone,
      });
      if (response && response.ok && Array.isArray(response.data)) {
        days.value = response.data;
        // Reasignar options para que apexcharts tome los nuevos labels
        chartOptions.value = {
          ...chartOptions.value,
          labels: response.data.map((d, i) => (i === 0 ? "Today" : d.label)),
        };
      }
    });

    return { series, chartOptions };
  },
};
</script>
